import { find, map, extend } from 'lodash';
import { prop } from '@core/lib/fp';
import {
  isInSetupPhase,
  hasFeature,
  hasNoPaymentDetails,
  hasFraudPackage,
  hasPurchaseVerificationPackage,
  hasMultitouchPackage,
  hasSegmentationPackage,
  hasUninstallPackage,
  hasAdRevenuePackage,
  hasRevenueDataFeature,
  hasControlCenterPackage,
  hasSecurityPackage,
  hasAdvancedPermissionsPackage,
  hasSubscriptionPackage,
  hasNewFeatureScope,
} from '@core/lib/utils/account';
import {
  getCurrentAccount,
  getTerms,
  acceptTerms,
  rejectTerms
} from '@core/api/account';

function Account($q, Api, billingCurrencies) {
  'ngInject';

  return {
    // Proxied functions
    isInSetupPhase,
    hasFeature,
    hasNoPaymentDetails,
    hasFraudPackage,
    hasPurchaseVerificationPackage,
    hasMultitouchPackage,
    hasSegmentationPackage,
    hasUninstallPackage,
    hasAdRevenuePackage,
    hasRevenueDataFeature,
    hasControlCenterPackage,
    hasSecurityPackage,
    hasAdvancedPermissionsPackage,
    hasSubscriptionPackage,
    hasNewFeatureScope,

    // Own functions
    finishSetup,
    getCurrent,
    getCurrentForcingUpdate,
    getAccounts,
    queryAccounts,
    getLead,
    signup,
    update,
    getAuditEvents,
    getInvoices,
    getCurrentCharge,
    getPaymentPlans,
    requestContact,
    updatePlan,
    getTwitterAccounts,
    acceptTwitterTerms,
    getTwitterAccount,
    refreshTwitterAccount,
    updateTwitterAdsAccounts,
    deleteTwitterAccount,
    getUsers,
    getCurrencies,
    setMainContact,
    getPaymentMethod,
    getTiktokAccounts,
    getTiktokAccount,
    refreshTiktokAccount,
    updateTiktokAccount,
    deleteTiktokAccount,
    getApplovinAccounts,
    getApplovinAccount,
    checkApplovinReportKey,
    checkApplovinManagementKey,
    unlinkApplovinAccount,
    createApplovinAccount,
    updateApplovinAccount,
    getPartnerAccounts,
    getAppleSearchAdAccounts,
    getAppleSearchAdAccount,
    unlinkAppleSearchAdAccount,
    updateAppleSearchAdAccount,
    refreshAppleSearchAdAccount,
    getTerms: _getTerms,
    activate,
    getTermsStatus,
    acceptTerms: _acceptTerms,
    attemptSwitchUsingToken,
    getNewTermsStatus,
    getNewTermsStatusForceUpdate,
    acceptNewTerms,
    rejectNewTerms
  };

  /**
   * Gets current payment method of the current account
   *
   * @returns {Promise}
   */
  function getPaymentMethod() {
    return Api.paymentMethod
      .query()
      .$promise.then(result => find(result, { default: true }));
  }

  /**
   * Gets list of available currencies
   *
   * @param {String} vendor
   * @returns {Array}
   */
  function getCurrencies(vendor) {
    return billingCurrencies[vendor];
  }

  /**
   * Gets current charge of the current account
   *
   * @returns {Promise}
   */
  function getCurrentCharge() {
    return Api.currentCharge
      .get()
      .$promise.then(result => result.data.attributes);
  }

  /**
   * Gets invoices of the current account
   *
   * @param {Number} limit
   * @returns {Promise}
   */
  function getInvoices(limit) {
    return Api.invoice
      .get(limit ? { "page[limit]": limit } : undefined)
      .$promise.then(result => map(result.data, 'attributes'));
  }

  /**
   * Updates account with the given id
   *
   * @param {Number} id
   * @param {Object} account
   * @returns {Promise}
   */
  function update(id, account) {
    return Api.account.update({ id: id }, account).$promise;
  }

  /**
   * Gets signup lead by token
   *
   * @param {String} leadToken
   * @param {Boolean} useNewPricing
   * @returns {Promise}
   */
  function getLead(leadToken, useNewPricing) {
    return Api.signup.get({
      token: leadToken,
      new_pricing: useNewPricing
    }).$promise;
  }

  /**
   * Signups or just validate the signup
   *
   * @param {object} signupData
   * @param {Boolean} validateOnly
   * @param {Boolean} useNewPricing
   * @returns {Promise}
   */
  function signup(signupData, validateOnly, useNewPricing) {
    return Api.signup.save(
      extend({}, signupData, {
        validate_only: validateOnly,
        new_pricing: useNewPricing
      })
    ).$promise;
  }

  /**
   * Updates account state to waiting_for_data
   *
   * @param {Number} id
   * @returns {Promise}
   */
  function finishSetup(id) {
    return Api.account.update({ id: id }, { finish_setup: true }).$promise;
  }

  /**
   * Gets current account, and take it from cache if available
   *
   * @returns {Promise}
   */
  function getCurrent() {
    return $q.when(getCurrentAccount());
  }

  /**
   * @returns {Promise}
   */
  function getCurrentForcingUpdate() {
    return $q.when(getCurrentAccount({ forceUpdate: true }));
  }

  /**
   * Gets available account of the current user
   *
   * @returns {Promise}
   */
  function getAccounts() {
    return Api.accountDashboard.get().$promise.then(res => res.accounts || []);
  }

  /**
   * Queries accounts by search term
   *
   * @param {String} term
   * @returns {Promise}
   */
  function queryAccounts(term) {
    return Api.accountSwitch.query({
      search: term
    }).$promise;
  }

  /**
   * Gets audit events
   *
   * @param {Number} offset
   * @param {Number} limit
   * @param {Boolean} isSuperAdmin
   * @param {string=} term
   * @returns {Promise}
   */
  function getAuditEvents(offset, limit, isSuperAdmin, term) {
    var params = {
      'page[offset]': offset,
      'page[limit]': limit
    };

    if (term) {
      params['filter[search]'] = term;
    }

    if (isSuperAdmin) {
      params['filter[with_internal]'] = isSuperAdmin;
    }

    return Api.audit.get(params).$promise;
  }

  /**
   * Gets payment plans for particular account
   *
   * @param {Number} accountId
   * @returns {Promise}
   */
  function getPaymentPlans(accountId) {
    return Api.account.query({ action: 'payment_plans', id: accountId })
      .$promise;
  }

  /**
   * Requests contact of the account manager
   *
   * @param {Number} accountId
   * @returns {Promise}
   */
  function requestContact(accountId) {
    return Api.account.post({ id: accountId, action: 'contact_request' })
      .$promise;
  }

  /**
   * Updates pricing plan
   *
   * @param {Number} accountId
   * @param {Number} planId
   * @returns {Promise}
   */
  function updatePlan(accountId, planId, currentPlan) {
    return Api.account.update({
      id: accountId,
      planId: planId,
      action: 'payment_plans',
      current_plan: currentPlan
    }).$promise;
  }

  /**
   * Gets twitter handles
   *
   * @returns {Promise}
   */
  function getTwitterAccounts() {
    return Api.twitterSettings.get({
      scope: 'twitter_ads',
      action: 'accounts'
    }).$promise;
  }

  /**
   * Fetches twitter account data which in the same time validates it
   *
   * @param {Number} id
   * @returns {Promise}
   */
  function getTwitterAccount(id) {
    return Api.twitterSettings.get({
      scope: 'twitter',
      action: 'accounts',
      id: id
    }).$promise;
  }

  /**
   * Refreshes the twitter accounts list
   *
   * @param {Number} id
   * @returns {Promise}
   */
  function refreshTwitterAccount(id) {
    return Api.twitterSettings.get({
      scope: 'twitter',
      action: 'accounts',
      id: id,
      subAction: 'renew'
    }).$promise;
  }

  /**
   * Updates twitter ads account with new states
   *
   * @param {Object} states
   * @returns {Promise}
   */
  function updateTwitterAdsAccounts(states) {
    return Api.twitterSettings.batch(
      {
        scope: 'twitter_ads',
        action: 'accounts',
        subAction: 'batch'
      },
      {
        ads_accounts: states
      }
    ).$promise;
  }

  /**
   * Deletes twitter account
   *
   * @param {Number} id
   * @returns {Promise}
   */
  function deleteTwitterAccount(id) {
    return Api.twitterSettings.remove({
      scope: 'twitter',
      action: 'accounts',
      id: id
    }).$promise;
  }

  /**
   * Accepts the twitter updated terms for existing users
   *
   * @returns {Promise}
   */
  function acceptTwitterTerms() {
    return Api.twitterSettings.post({
      scope: 'twitter',
      action: 'terms_and_conditions',
      subAction: 'accept'
    }).$promise;
  }

  /**
   * Gets partner handles
   *
   * @param {String} partnerId
   * @returns {Promise}
   */
  function getPartnerAccounts(partnerId) {
    return Api.partner.query({ action: 'accounts', partnerId }).$promise;
  }

  /**
   * Gets list of users for particular account
   *
   * @returns {Promise}
   */
  function getUsers() {
    return getCurrent().then(function(account) {
      return Api.user.query({ account_id: account.id }).$promise;
    });
  }

  /**
   * Sets a user as the main contact
   *
   * @param {Number} accountId
   * @param {Number} userId
   * @returns {Promise}
   */
  function setMainContact(accountId, userId) {
    return Api.account.update({ id: accountId, main_contact_id: userId })
      .$promise;
  }

  /**
   * Gets list of tiktok accounts
   *
   * @returns {Promise}
   */
  function getTiktokAccounts() {
    return Api.tiktok.get({ action: 'accounts' }).$promise;
  }

  /**
   * Gets tiktok account
   *
   * @param {Number} id
   * @returns {Promise}
   */
  function getTiktokAccount(id) {
    return Api.tiktok.get({ action: 'accounts', id  }).$promise;
  }

  /**
   * Refreshes the tiktok accounts list
   *
   * @param {Number} id
   * @returns {Promise}
   */
  function refreshTiktokAccount(id) {
    return Api.tiktok.post({
      action: 'ad_accounts',
      id: id,
      subAction: 'renew'
    }).$promise;
  }

  /**
   * Update tiktok ad account and ad accounts
   *
   * @param {String} id
   * @param {Object} credential
   * @returns {Promise}
   */
  function updateTiktokAccount(id, states) {
    return Api.tiktok.save(
      { action: 'ad_accounts' },
      { id: id, ad_accounts: states.ad_accounts }
    ).$promise;
  }

  /**
   * Deletes tiktok account
   *
   * @param {Number} id
   * @returns {Promise}
   */
  function deleteTiktokAccount(id) {
    return Api.tiktok.remove({ action: 'accounts', id }).$promise;
  }

  /**
   * Get list of apple search ad accounts
   *
   * @returns {Promise}
   */
  function getAppleSearchAdAccounts() {
    return Api.appleSearchAds.get({
      action: 'accounts'
    }).$promise;
  }

  /**
   * Get single apple search ad account
   *
   * @param {Number} id
   * @returns {Promise}
   */
  function getAppleSearchAdAccount(id) {
    return Api.appleSearchAds.get({
      id,
      action: 'accounts'
    }).$promise;
  }

  /**
   * Unlinks apple search ad account
   *
   * @param {Number} id
   * @returns {Promise}
   */
  function unlinkAppleSearchAdAccount(id) {
    return Api.appleSearchAds.remove({
      id,
      action: 'accounts'
    }).$promise;
  }

  /**
   * Updates apple search ad account
   *
   * @param {Object} states
   * @returns {Promise}
   */
  function updateAppleSearchAdAccount(states) {
    return Api.appleSearchAds.batch(
      {
        action: 'ads_accounts',
        subAction: 'toggle'
      },
      {
        ads_accounts: states
      }
    ).$promise;
  }

  /**
   * @param {Number} id
   * @returns {Promise}
   */
  function refreshAppleSearchAdAccount(id) {
    return Api.appleSearchAds.post({
      action: 'accounts',
      id: id,
      subAction: 'renew'
    }).$promise;
  }

  /**
   * Fetches the latest version of Terms & Conditions on adjust.com
   *
   * @param {String} terms
   * @param {String} lang
   * @returns {Promise}
   */
  function _getTerms(terms, lang = "en") {
    return Api.terms.get({ action: terms, lang: lang })
      .$promise.catch(() =>
        $q.reject({
          errors: "terms_not_available"
        })
      );
  }

  /**
   * Transitions account from `waiting_for_data` to `active`
   *
   * @returns {Promise}
   */
  function activate(id) {
    return Api.account.patch(
      {
        action: 'activate',
        id: id
      },
      null
    ).$promise;
  }

  /**
   * Gets terms status, is signed or not
   *
   * @param {Number} appId
   * @param {String} name
   * @returns {Promise}
   */
  function getTermsStatus(appId, name) {
    return Api.termsConfirm.get({
      mobile_app_id: appId,
      term_name: name
    }).$promise;
  }

  /**
   * Accepts particular terms
   *
   * @param {Number} appId
   * @param {String} name
   * @returns {Promise}
   */
  function _acceptTerms(appId, name) {
    return Api.termsConfirm.create({
      action: 'accept',
      mobile_app_id: appId,
      term_name: name
    }).$promise;
  }

  /**
   * @param {String} token
   * @returns {Promise<Number>}
   */
  function attemptSwitchUsingToken(token) {
    return Api.accountSwitchViaToken
      .query({ token })
      .$promise.then(prop('status'));
  }

  /**
   * @returns {Promise<TermStatus>}
   */
  function getNewTermsStatus() {
    return $q.when(getTerms());
  }

  /**
   * @returns {Promise<TermStatus>}
   */
  function getNewTermsStatusForceUpdate() {
    return $q.when(getTerms({ forceUpdate: true }));
  }

  /**
   * @param {String} token
   * @returns {Promise<>}
   */
  function acceptNewTerms(token) {
    return $q.when(acceptTerms(token));
  }

  /**
   * @param {String} token
   * @returns {Promise<>}
   */
  function rejectNewTerms(token) {
    return $q.when(rejectTerms(token));
  }

  /**
   * Get list of applovin accounts
   *
   * @returns {Promise}
   */
  function getApplovinAccounts() {
    return Api.applovin.get({
      action: 'accounts'
    }).$promise;
  }

  /**
   * Get applovin account
   *
   * @returns {Promise}
   */
  function getApplovinAccount(id) {
    return Api.applovin.get({ action: 'accounts', id }).$promise;
  }

  /**
   * Verify the applovin report key
   *
   * @param {String} key
   * @returns {Promise}
   */
  function checkApplovinReportKey(key) {
    return Api.applovin.get({
      action: 'accounts',
      subAction: 'check_report_key',
      key
    }).$promise;
  }

  /**
   * Verify the applovin management key
   *
   * @param {String} key
   * @returns {Promise}
   */
  function checkApplovinManagementKey(key) {
    return Api.applovin.get({
      action: 'accounts',
      subAction: 'check_management_key',
      key
    }).$promise;
  }

  /**
   * Unlink applovin ad account
   *
   * @param {String} id
   * @returns {Promise}
   */
  function unlinkApplovinAccount(id) {
    return Api.applovin.remove({
      id,
      action: 'accounts'
    }).$promise;
  }

  /**
   * Create applovin ad account
   *
   * @param {Object} credential
   * @returns {Promise}
   */
  function createApplovinAccount(credential) {
    return Api.applovin.post(
      {
        action: 'accounts'
      },
      {
        credential
      }
    ).$promise;
  }

  /**
   * Update applovin ad account
   *
   * @param {String} id
   * @param {Object} credential
   * @returns {Promise}
   */
  function updateApplovinAccount(id, credential) {
    return Api.applovin.patch(
      {
        action: 'accounts',
        id
      },
      {
        credential
      }
    ).$promise;
  }
}

export { Account };
